import { Directory, Filesystem } from '@capacitor/filesystem';
import { IS_MOBILE_APP } from 'const';
import { FileOpener } from '@capacitor-community/file-opener';
import { encode } from 'uint8-to-base64';

const MOBILE_CACHE_PATH = 'equinem_cache';

/**
 * Download function for on a mobile App
 * This function will save the files to the local filesystem and return the file path
 *
 * @param response
 * @param filename
 */
async function downloadForMobile(response: Response, filename: string): Promise<string> {
  if (!IS_MOBILE_APP) {
    throw new Error('downloadForMobile() is only supported for MOBILE_APP');
  }

  // NOTE: we do not need to ask for File Permission
  // The cache folder is always writeable for both IOS and Andrid
  // @see https://capacitorjs.com/docs/v4/apis/filesystem#requestpermissions

  // First delete the cache dir to avoid stacking all (huge) temp files here
  // We are not sure when Android or IOS clear the cache folder, so to be sure we remove the files by our self
  try {
    await Filesystem.rmdir({
      directory: Directory.Cache,
      recursive: true,
      path: MOBILE_CACHE_PATH,
    });
  } catch (error) {
    // ignore errors
    console.error('Failed to remove cache directory:', error);
  }

  // (re)create the cache folder
  try {
    await Filesystem.mkdir({
      directory: Directory.Cache,
      path: MOBILE_CACHE_PATH,
    });
  } catch (e) {
    console.error('Failed to creat cache directory:', e);
    throw new Error('Cannot create a cache dir');
  }

  const arrayBuffer = await response.arrayBuffer();

  // convert the uint8Array to base64
  // TODO --> https://gitlab.equinem.com/equinem/equiapp/-/issues/801
  // eslint-disable-next-line
  // @ts-ignore
  const base64Data = encode(new Uint8Array(arrayBuffer));
  console.log(`${MOBILE_CACHE_PATH}/${filename}`);
  try {
    // it seems that in Capcacitor 7 the result of Filesystem.writeFile return an trailing slash in the filename file
    // e.g. "file:///data/user/0/com.equinem.equiapp/cache/equinem_cache/filename.jpg/"
    // in order to fix this we should not rely on the returned file path
    // but we build the path our self
    // @see https://github.com/ionic-team/capacitor-filesystem/issues/17
    await Filesystem.writeFile({
      path: `${MOBILE_CACHE_PATH}/${filename}`,
      directory: Directory.Cache,
      data: base64Data,
    });

    // fetch the path of the cache folder
    const basePath = await Filesystem.getUri({
      path: MOBILE_CACHE_PATH,
      directory: Directory.Cache,
    });

    return `${basePath.uri}${filename}`;
  } catch (e) {
    console.error(e);
    const error = e instanceof Error ? e.message : 'unknown error';
    throw new Error(`Cannot write files to ${filename} with error: ${error}`);
  }
}

/**
 * Download function based on a url for on a mobile App
 * @param url
 * @param abortSignal
 * @returns
 */
async function downloadUrlForMobile(url: string, abortSignal?: AbortSignal): Promise<string> {
  if (!IS_MOBILE_APP) {
    throw new Error('downloadUrlForMobile() is only supported for MOBILE_APP');
  }

  // first fetch the file
  const response = await fetch(url, { signal: abortSignal });
  if (!response.ok) {
    throw new Error(`Failed to fetch the file from ${url}`);
  }

  // get the correct Filename from the path
  const match = url.match(/\/([^/?]+)\?/);
  const filename = match ? match[1] : 'image';

  return downloadForMobile(response, filename);
}

/**
 * Open a file in a native dialog for mobile
 *
 * @param response
 * @param filename
 * @returns
 */
export async function openFileForMobile(response: Response, filename: string): Promise<void> {
  if (!IS_MOBILE_APP) {
    throw new Error('openFileForMobile() is only supported for MOBILE_APP');
  }

  // download all files locally
  const filePath = await downloadForMobile(response, filename);

  // show the native IOS/Android File preview dialog
  try {
    await FileOpener.open({ filePath });
  } catch (error) {
    console.error('Failed to open file for mobile:', error);
    throw new Error(`Cannot open file ${filename} with error: ${error instanceof Error ? error.message : 'unknown error'}`);
  }
}

/**
 * Open a file in a native dialog for mobile
 *
 * @param url
 * @param abortSignal
 * @returns
 */
export async function openFileByUrlForMobile(url: string, abortSignal?: AbortSignal): Promise<void> {
  if (!IS_MOBILE_APP) {
    throw new Error('openFileByUrlForMobile() is only supported for MOBILE_APP');
  }

  // download all files locally
  const filePath = await downloadUrlForMobile(url, abortSignal);

  // show the native IOS/Android File preview dialog
  FileOpener.open({ filePath });
}

/**
 * Simple function to download a file from a given url.
 * @param fileUrl
 */
export const downloadFileByUrl = async (fileUrl: string): Promise<void> => {
  // for mobile we use a native way of opening files
  if (IS_MOBILE_APP) {
    return await openFileByUrlForMobile(fileUrl);
  }

  // <a href="path_to_file" download="proposed_file_name">Download</a>
  // In order to start a file download we need to pull a little trick.
  // We have to create an 'a' html element with the file content attached.
  // Inspired by: https://stackoverflow.com/a/42274086
  const a = document.createElement('a');
  a.href = fileUrl;

  // a.download = filename(t, selectedFile);
  // We need to append the element to the dom -> otherwise it will not
  // work in firefox.
  document.body.appendChild(a);
  a.click();
  // afterwards we remove the element again.
  a.remove();
};

/**
 * Simple donwload download a blob as file
 *
 * @param {Response}response
 * @param name
 */
export const downloadFile = async (response: Response, name: string): Promise<void> => {
  // for mobile we use a native way of opening files
  if (IS_MOBILE_APP) {
    return await openFileForMobile(response, name);
  }

  // In order to start a file download we need to pull a little trick.
  // We have to create an 'a' html element with the file content attached.
  // Inspired by: https://stackoverflow.com/a/42274086
  const a = document.createElement('a');
  const blob = await response.blob();
  a.href = window.URL.createObjectURL(blob);
  a.download = name;
  // we need to append the element to the dom -> otherwise it will not
  // work in firefox.
  document.body.appendChild(a);

  a.click();
  // afterwards we remove the element again.
  a.remove();
};
